import {
  Chip,
  ChipColorEnum,
  ChipShapeEnum,
  ChipSizeEnum,
  Column,
  ImageThumbnail,
  ImageThumbnailSizesEnum,
  TablePaginated,
  TablePaginatedOptions,
  Tooltip,
  TrashDeleteIcon,
  Typography,
} from '@acceso/design-system';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { StateActiveInTable } from '@/components/StateActiveInTable/StateActiveInTable';
import { TableRow } from '@/components/TableRow';
import { TableUpdatedBy } from '@/components/TableUpdatedBy';
import { useGetLabels } from '@/features/labels/api/get-labels';
import { useUserOptions } from '@/features/users/hooks/useUserOptions';
import { useStateOptions } from '@/hooks/useStateOptions';
import { useTableMultiselect } from '@/hooks/useTableMultiselect';
import { State } from '@/types';
import { createTableFilterOptions } from '@/utils/table-filters';

import { useLogosFilters } from '../../stores/logos-filters';
import { TLogo } from '../../types';

interface LogosTableProps {
  items: TLogo[];
  usersIds: string[];
  totalItems: number;
  activeItem: TLogo | null;
  onUpdate?: (logos: TLogo) => void;
  onDuplicate?: (logos: TLogo) => void;
  onDelete?: (logos: TLogo) => void;
  onSelect?: (logos: TLogo) => void;
  onMultiDelete?: (logos: TLogo[]) => void;
  setSelectedUser: Dispatch<SetStateAction<TLogo | null>>;
  isFetching?: boolean;
}

export function LogosTable({
  items,
  usersIds,
  totalItems,
  activeItem,
  onUpdate,
  onDuplicate,
  onDelete,
  onSelect,
  onMultiDelete,
  isFetching,
}: LogosTableProps) {
  const { t } = useTranslation();
  const { setFilters, paginationValues, setPage, setRows, setSortBy, setSearchQuery } =
    useLogosFilters();
  const userOptions = useUserOptions({
    usersIds,
  });
  const stateOptions = useStateOptions();

  const { data: labelsData } = useGetLabels();

  const labelsMap = useMemo(() => {
    const map = new Map<string, string>();
    labelsData?.forEach(label => {
      map.set(label.id, label.name);
    });
    return map;
  }, [labelsData]);

  const { checkboxColumn, multiSelectionButtonProps } = useTableMultiselect<TLogo>({
    itemsCount: items.length,
    actions: [
      {
        icon: TrashDeleteIcon,
        onClick: selection => {
          onMultiDelete?.(selection.selectedItems);
        },
        tooltip: t('Eliminar'),
      },
    ],
  });

  const options: Partial<TablePaginatedOptions> = {
    pagination: {
      changePage: setPage,
      changeRows: setRows,
      currentPage: paginationValues.currentPage,
      itemsPerPage: paginationValues.rowsPerPage,
    },
    setSortBy,
    sortBy: paginationValues.sortBy,
    onSearchChange: setSearchQuery,
    filters: [
      {
        name: 'labels',
        placeholder: t('Etiquetas'),
        width: 'w-52',
        isMultiple: true,
        options:
          labelsData
            ?.sort((a, b) => a.name.localeCompare(b.name))
            .map(({ name, id }) => ({ label: name, value: id })) ?? [],
        onChange: value => {
          const values = Array.isArray(value) ? value : value ? [value] : [];
          setFilters({ labelsIn: values.length > 0 ? values : undefined });
        },
      },
      {
        name: 'state',
        placeholder: t('Estado'),
        options: createTableFilterOptions({
          options: stateOptions,
          allOptionLabel: t('Cualquier estado'),
        }),
        onChange: value => {
          setFilters({
            stateIn: [value as unknown as State],
          });
        },
      },
      {
        name: 'updated_by',
        placeholder: t('Actualizado por'),
        options: createTableFilterOptions({
          options: userOptions,
          allOptionLabel: t('Actualizado por cualquiera'),
        }),
        onChange: value => {
          setFilters({
            updatedByIn: [value as unknown as string],
          });
        },
      },
    ],
    searchBarPlaceholder: t('Buscar por nombre de marca o descricpión'),
    multiselect: multiSelectionButtonProps,
  };

  const columns: Column<TLogo>[] = useMemo(
    (): Column<TLogo>[] => [
      checkboxColumn,
      {
        id: 'logo',
        sortable: true,
        title: t('Logo'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return (
            <ImageThumbnail
              size={ImageThumbnailSizesEnum.Xxl}
              src={item.parameters?.main_logo_presigned_url}
            />
          );
        },
      },
      {
        id: 'name',
        sortable: true,
        title: t('Marca'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return <TableRow showTooltip content={item.name || ''} />;
        },
      },
      {
        id: 'description',
        sortable: true,
        title: t('Descripción'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return <TableRow content={item.description || ''} />;
        },
      },
      {
        id: 'active_detection',
        sortable: true,
        title: t('Detección Imagen'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return <TableRow title={item.active_detection ? t('SI') : t('NO')} />;
        },
      },
      {
        id: 'labels',
        sortable: true,
        title: t('Etiquetas'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          const labels = item.labels?.map(labelId => labelsMap.get(labelId)).filter(Boolean);
          const displayedLabels = labels?.slice(0, 2) || [];
          const remainingLabels = labels?.slice(2) || [];

          return (
            <div className="flex flex-wrap items-center gap-1">
              {displayedLabels.map(label => (
                <Chip
                  key={label}
                  shape={ChipShapeEnum.ROUNDED}
                  size={ChipSizeEnum.XS}
                  color={ChipColorEnum.DEFAULT}
                >
                  {label}
                </Chip>
              ))}
              {remainingLabels.length > 0 && (
                <Tooltip help={remainingLabels.join(', ')} grow>
                  <Typography>+{remainingLabels.length}</Typography>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        id: 'state',
        sortable: true,
        title: t('Estado'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return <StateActiveInTable status={item.state} />;
        },
      },
      {
        id: 'updated_by',
        sortable: true,
        title: t('Actualizado por'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return <TableUpdatedBy item={item} />;
        },
      },
      {
        id: 'actions',
        title: '',
        className: 'items-end',
        width: '5%',
        render: item => (
          <RenderActionButtons
            item={item}
            onUpdate={() => onUpdate?.(item)}
            onDuplicate={() => onDuplicate?.(item)}
            onDelete={() => onDelete?.(item)}
          />
        ),
      },
    ],
    [t, checkboxColumn, onSelect, onUpdate, onDelete]
  );

  return (
    <TablePaginated
      data={items}
      totalItems={totalItems}
      options={options}
      columns={columns}
      activeItem={activeItem}
      isFetching={isFetching}
      ignoreEmpty
    />
  );
}
